import { useEffect, useRef, useState } from "react"

import { getTracking } from "../api/tracking.services.ts"
import { queryForView } from "../helpers/manager-views.ts"
import {
  MANAGER_VIEWS,
  type ManagerView,
  type TrackingQuery
} from "../types/tracking.types.ts"

export type ViewCounts = Partial<Record<ManagerView, number>>

/**
 * Every view's total, asked of the server under the same scope and filters as
 * the table — never counted off the rows already loaded, which are one page.
 * A view whose count failed is left out rather than shown as zero.
 */
export function useViewCounts(
  base: TrackingQuery,
  views: readonly ManagerView[] = MANAGER_VIEWS,
  /** Bumped when a row action changed what the views hold. */
  generation = 0
) {
  const [counts, setCounts] = useState<ViewCounts>({})
  const latest = useRef(0)
  const { ordering: _ordering, page: _page, ...scope } = base
  const key = JSON.stringify([scope, views, generation])

  useEffect(() => {
    const id = ++latest.current
    void Promise.all(
      views.map(async (view) => {
        const result = await getTracking({
          ...queryForView(view, scope),
          page_size: 1
        })
        return [
          view,
          result.ok && typeof result.data?.count === "number"
            ? result.data.count
            : null
        ] as const
      })
    ).then((answers) => {
      if (id !== latest.current) return
      setCounts(
        Object.fromEntries(
          answers.filter(([, count]) => count !== null)
        ) as ViewCounts
      )
    })
    // `key` stands for `scope` and `views`, rebuilt on every render.
  }, [key])

  return counts
}
